import React, { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { CalendarCheck, ChevronDown, Coffee, Play, Square } from 'lucide-react'
import { format } from 'date-fns'
import { attendanceApi, type LateConfirmationPayload } from '../../api/attendance'
import { Modal } from '../common/Modal'
import { Button } from '../common/Button'
import type { AttendanceLateType, AttendanceRecord } from '../../types'

const MOBILE_MESSAGE = 'Please use your laptop or desktop to add attendance time.'
type AttendanceAction = 'start-day' | 'start-break' | 'end-break' | 'finish-day'

const DAILY_MOTIVATIONS = [
  'Start with focus. Finish with progress.',
  'Small wins today become big results tomorrow.',
  'Bring your best energy to the first task.',
  'A clear start makes the whole day easier.',
  'One focused hour can change the whole day.',
  'Start steady. Build momentum.',
  'Today is a good day to make useful progress.',
  'Show up with intent. Let the results follow.',
  'Begin with clarity, then keep moving.',
  'Your effort today moves the team forward.',
  'Take the first step. The rhythm will follow.',
  'Progress starts when you do.',
  'Start calm. Work sharp.',
  'Make today count in small, real ways.',
  'Focus on the next useful thing.',
  'A strong start creates a smoother finish.',
  'Your consistency is your advantage.',
  'Start with purpose. Keep it simple.',
  'Good work begins with a clear beginning.',
  'Turn today’s plan into visible progress.',
  'Bring patience, focus, and good judgment.',
  'Begin well. Improve as you go.',
  'Every good delivery starts with showing up.',
  'Give the day a clean start.',
  'Focus first. Speed follows.',
  'Start with care. Build with confidence.',
  'Move one task closer to done.',
  'Let today’s work speak clearly.',
  'Start strong, stay steady, finish proud.',
  'Your best work starts with one intentional click.',
]

function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(navigator.userAgent)
}

function apiError(error: unknown): string {
  return (error as { response?: { data?: { error?: string } } })?.response?.data?.error
    || (error as Error)?.message
    || 'Something went wrong.'
}

function isLateConfirmation(error: unknown): boolean {
  return (error as { response?: { status?: number; data?: { code?: string } } })?.response?.status === 409
    && (error as { response?: { data?: { code?: string } } })?.response?.data?.code === 'LATE_CONFIRMATION_REQUIRED'
}

function isWorkLogReasonRequired(error: unknown): boolean {
  return (error as { response?: { status?: number; data?: { code?: string } } })?.response?.status === 409
    && (error as { response?: { data?: { code?: string } } })?.response?.data?.code === 'WORK_LOG_REASON_REQUIRED'
}

function workLogRequirement(error: unknown) {
  return (error as { response?: { data?: { data?: { workLogMinutes?: number; targetWorkedMinutes?: number; message?: string } } } })?.response?.data?.data
}

function formatTime(value?: string | null) {
  if (!value) return '-'
  return format(new Date(value), 'h:mm a')
}

function formatMinutes(value?: number | null) {
  if (!value || value <= 0) return '0h'
  const hours = Math.floor(value / 60)
  const minutes = value % 60
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
}

function scheduledStart(record: AttendanceRecord) {
  return new Date(`${record.attendanceDateKey}T${record.shiftStartTime}:00`).getTime()
}

function totalBreakMinutes(record?: AttendanceRecord, creditedStart = 0, creditedEnd = Number.MAX_SAFE_INTEGER) {
  if (!record) return 0
  return record.breaks.reduce((total, attendanceBreak) => {
    if (!attendanceBreak.endedAt) return total
    const start = Math.max(new Date(attendanceBreak.startedAt).getTime(), creditedStart)
    const end = Math.min(new Date(attendanceBreak.endedAt).getTime(), creditedEnd)
    return end > start ? total + Math.round((end - start) / 60000) : total
  }, 0)
}

function currentWorkedMinutes(record?: AttendanceRecord) {
  if (!record?.startedAt) return 0
  if (record.totalWorkedMinutes != null) return record.totalWorkedMinutes
  const end = record.finishedAt ? new Date(record.finishedAt).getTime() : Date.now()
  const creditedStart = Math.max(new Date(record.startedAt).getTime(), scheduledStart(record))
  return Math.max(0, Math.round((end - creditedStart) / 60000) - totalBreakMinutes(record, creditedStart, end))
}

function dailyMotivation(record?: AttendanceRecord) {
  const key = `${record?.userId || 'user'}:${record?.attendanceDateKey || new Date().toISOString().slice(0, 10)}`
  const hash = Array.from(key).reduce((sum, char) => sum + char.charCodeAt(0), 0)
  return DAILY_MOTIVATIONS[hash % DAILY_MOTIVATIONS.length]
}

function statusLabel(record?: AttendanceRecord) {
  if (!record?.startedAt) return 'Not started'
  if (record.finishedAt) return 'Finished'
  if (record.breaks.some((attendanceBreak) => !attendanceBreak.endedAt)) return 'On break'
  return 'Working'
}

function nextActions(record?: AttendanceRecord): AttendanceAction[] {
  if (!record?.startedAt) return ['start-day']
  if (record.finishedAt) return []
  if (record.breaks.some((attendanceBreak) => !attendanceBreak.endedAt)) return ['end-break']
  return ['start-break', 'finish-day']
}

export const AttendanceWidget: React.FC = () => {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [lateOpen, setLateOpen] = useState(false)
  const [lateType, setLateType] = useState<AttendanceLateType>('PERMISSION')
  const [lateReason, setLateReason] = useState('')
  const [ackChecked, setAckChecked] = useState(false)
  const [ackText, setAckText] = useState('')
  const [finishReasonOpen, setFinishReasonOpen] = useState(false)
  const [finishReason, setFinishReason] = useState('')
  const [finishRequirement, setFinishRequirement] = useState<{ workLogMinutes?: number; targetWorkedMinutes?: number; message?: string } | null>(null)

  const todayQuery = useQuery({
    queryKey: ['attendance', 'today'],
    queryFn: () => attendanceApi.getToday().then((res) => res.data.data),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  })

  const record = todayQuery.data

  useEffect(() => {
    if (!open) return
    const handler = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['attendance'] })
  }

  const startMutation = useMutation({
    mutationFn: (payload?: LateConfirmationPayload) => attendanceApi.startDay(payload),
    onSuccess: () => {
      setMessage('')
      setLateOpen(false)
      setOpen(false)
      invalidate()
    },
    onError: (error) => {
      if (isLateConfirmation(error)) {
        setLateOpen(true)
        return
      }
      setMessage(apiError(error))
    },
  })

  const startBreakMutation = useMutation({
    mutationFn: () => attendanceApi.startBreak(),
    onSuccess: invalidate,
    onError: (error) => setMessage(apiError(error)),
  })

  const endBreakMutation = useMutation({
    mutationFn: () => attendanceApi.endBreak(),
    onSuccess: invalidate,
    onError: (error) => setMessage(apiError(error)),
  })

  const finishMutation = useMutation({
    mutationFn: (payload?: { workLogShortfallReason: string }) =>
      payload ? attendanceApi.finishDayWithReason(payload) : attendanceApi.finishDay(),
    onSuccess: () => {
      setFinishReasonOpen(false)
      setFinishReason('')
      setFinishRequirement(null)
      invalidate()
    },
    onError: (error) => {
      if (isWorkLogReasonRequired(error)) {
        setFinishRequirement(workLogRequirement(error) || null)
        setFinishReasonOpen(true)
        return
      }
      setMessage(apiError(error))
    },
  })

  const guardDesktop = () => {
    if (!isMobileDevice()) return true
    setMessage(MOBILE_MESSAGE)
    setOpen(true)
    return false
  }

  const submitLate = () => {
    if (!guardDesktop()) return
    if (lateType === 'PERMISSION') {
      if (!lateReason.trim()) {
        setMessage('Reason is required for late start permission.')
        return
      }
      startMutation.mutate({ lateType, lateReason: lateReason.trim() })
      return
    }
    if (!lateReason.trim()) {
      setMessage('Reason is required for late start.')
      return
    }
    if (!ackChecked || ackText.trim().toUpperCase() !== 'I AGREE') {
      setMessage('Please tick the confirmation checkbox and type I AGREE.')
      return
    }
    startMutation.mutate({ lateType, lateReason: lateReason.trim(), lateAcknowledgedText: ackText.trim().toUpperCase() })
  }

  const submitFinishReason = () => {
    if (!finishReason.trim()) {
      setMessage('Please provide a reason before finishing your day.')
      return
    }
    finishMutation.mutate({ workLogShortfallReason: finishReason.trim() })
  }

  const runAction = (action: AttendanceAction) => {
    setMessage('')
    if (!guardDesktop()) return
    if (action === 'start-day') startMutation.mutate(undefined)
    if (action === 'start-break') startBreakMutation.mutate()
    if (action === 'end-break') endBreakMutation.mutate()
    if (action === 'finish-day') finishMutation.mutate(undefined)
  }

  const loading = startMutation.isPending || startBreakMutation.isPending || endBreakMutation.isPending || finishMutation.isPending
  const actions = nextActions(record)
  const primaryLabel = actions.includes('start-day')
    ? 'Start My Day'
    : actions.includes('end-break')
      ? 'End Break'
      : actions.includes('start-break')
        ? 'Start Break'
        : actions.includes('finish-day')
          ? 'Finish My Day'
          : statusLabel(record)
  const workedMinutes = currentWorkedMinutes(record)
  const targetMinutes = record?.targetWorkedMinutes || 480

  return (
    <>
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            height: 42,
            padding: '0 16px',
            borderRadius: 8,
            border: '1px solid #0f766e',
            background: record?.startedAt && !record.finishedAt ? '#0f766e' : '#0d9488',
            color: '#ffffff',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 800,
            whiteSpace: 'nowrap',
            boxShadow: '0 8px 18px rgba(13, 148, 136, 0.28)',
          }}
          title="Attendance"
        >
          <CalendarCheck size={15} />
          <span>{primaryLabel}</span>
          <ChevronDown size={13} />
        </button>

        {open && (
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 'calc(100% + 8px)',
              width: 300,
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              boxShadow: '0 12px 36px rgba(15,23,42,0.16)',
              zIndex: 1100,
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: 14, borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <strong style={{ color: '#0f172a', fontSize: 14 }}>
                  {record?.startedAt ? statusLabel(record) : 'Ready to start strong'}
                </strong>
                <span style={{ color: '#64748b', fontSize: 12 }}>{record?.shiftStartTime || '09:00'} {record?.timeZone || ''}</span>
              </div>
              {!record?.startedAt && (
                <div style={motivationStyle}>
                  <strong>{dailyMotivation(record)}</strong>
                  <span>Start your day when you are ready to focus.</span>
                </div>
              )}
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: '#475569', marginBottom: 5 }}>
                  <span>Attendance worked</span>
                  <strong style={{ color: '#0f172a' }}>{formatMinutes(workedMinutes)} / {formatMinutes(targetMinutes)}</strong>
                </div>
                <div style={{ height: 6, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min((workedMinutes / Math.max(targetMinutes, 1)) * 100, 100)}%`,
                      background: workedMinutes >= targetMinutes ? '#16a34a' : '#2563eb',
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12, fontSize: 12, color: '#475569' }}>
                <span>Punched in <strong style={metricStyle}>{formatTime(record?.startedAt)}</strong></span>
                <span>Finish <strong style={metricStyle}>{formatTime(record?.finishedAt)}</strong></span>
                <span>Break start <strong style={metricStyle}>{formatTime(record?.breaks.find((attendanceBreak) => !attendanceBreak.endedAt)?.startedAt || record?.breaks[record.breaks.length - 1]?.startedAt)}</strong></span>
                <span>Break end <strong style={metricStyle}>{formatTime(record?.breaks[record.breaks.length - 1]?.endedAt)}</strong></span>
              </div>
              {(record?.breaks.length || 0) > 0 && (
                <div style={breakListStyle}>
                  <div style={breakListTitleStyle}>Today's breaks</div>
                  {record?.breaks.map((attendanceBreak, index) => {
                    const duration = attendanceBreak.endedAt
                      ? Math.max(0, Math.round((new Date(attendanceBreak.endedAt).getTime() - new Date(attendanceBreak.startedAt).getTime()) / 60000))
                      : null
                    return (
                      <div key={attendanceBreak.id} style={breakRowStyle}>
                        <strong>Break {index + 1}</strong>
                        <span>{formatTime(attendanceBreak.startedAt)} - {attendanceBreak.endedAt ? formatTime(attendanceBreak.endedAt) : 'In progress'}</span>
                        <em>{duration == null ? 'Active' : `${duration} min`}</em>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12 }}>
              {actions.includes('start-day') && <ActionButton icon={<Play size={14} />} label="Start My Day" onClick={() => runAction('start-day')} disabled={loading} primary />}
              {actions.includes('start-break') && <ActionButton icon={<Coffee size={14} />} label="Start Break" onClick={() => runAction('start-break')} disabled={loading} />}
              {actions.includes('end-break') && <ActionButton icon={<Play size={14} />} label="End Break" onClick={() => runAction('end-break')} disabled={loading} />}
              {actions.includes('finish-day') && <ActionButton icon={<Square size={14} />} label="Finish My Day" onClick={() => runAction('finish-day')} disabled={loading} />}
              {actions.length === 0 && (
                <div style={{ color: '#16a34a', fontSize: 13, fontWeight: 700, padding: '6px 2px' }}>
                  Attendance completed for today.
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  navigate('/attendance')
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#2563eb',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 700,
                  textAlign: 'left',
                  padding: '6px 2px',
                }}
              >
                View attendance records
              </button>
              {message && <div style={{ color: '#dc2626', fontSize: 12, lineHeight: 1.4 }}>{message}</div>}
            </div>
          </div>
        )}
      </div>

      <Modal isOpen={lateOpen} onClose={() => setLateOpen(false)} title="Late Start Confirmation" width={560} closeOnBackdrop={false}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ margin: 0, color: '#475569', fontSize: 14, lineHeight: 1.5 }}>
            You are starting your day after your approved shift start time.
          </p>
          <p style={{ margin: 0, color: '#0f172a', fontSize: 14, fontWeight: 700 }}>Please select one option:</p>

          <label style={optionStyle}>
            <input type="radio" checked={lateType === 'PERMISSION'} onChange={() => setLateType('PERMISSION')} />
            <span>
              <strong>I have permission to start late today.</strong>
              {lateType === 'PERMISSION' && (
                <textarea
                  value={lateReason}
                  onChange={(event) => setLateReason(event.target.value)}
                  placeholder="Reason required"
                  rows={3}
                  style={textareaStyle}
                />
              )}
            </span>
          </label>

          <label style={optionStyle}>
            <input type="radio" checked={lateType === 'ACKNOWLEDGED'} onChange={() => setLateType('ACKNOWLEDGED')} />
            <span>
              <strong>I understand that starting late may impact my salary records and performance review.</strong>
              {lateType === 'ACKNOWLEDGED' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                  <textarea
                    value={lateReason}
                    onChange={(event) => setLateReason(event.target.value)}
                    placeholder="Reason required"
                    rows={3}
                    style={textareaStyle}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#475569' }}>
                    <input type="checkbox" checked={ackChecked} onChange={(event) => setAckChecked(event.target.checked)} />
                    Confirm acknowledgement
                  </label>
                  <input
                    value={ackText}
                    onChange={(event) => setAckText(event.target.value)}
                    placeholder="Type I AGREE"
                    style={inputStyle}
                  />
                </div>
              )}
            </span>
          </label>

          {message && <div style={{ color: '#dc2626', fontSize: 13 }}>{message}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Button variant="secondary" onClick={() => setLateOpen(false)}>Cancel</Button>
            <Button loading={startMutation.isPending} onClick={submitLate}>Submit</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={finishReasonOpen} onClose={() => setFinishReasonOpen(false)} title="Work Log Confirmation" width={560} closeOnBackdrop={false}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ margin: 0, color: '#475569', fontSize: 14, lineHeight: 1.5 }}>
            {finishRequirement?.message || `Looks like only ${formatMinutes(finishRequirement?.workLogMinutes)} is logged in your work log today. Please add/fix your work log details or provide a reason before finishing your day.`}
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <span style={summaryPillStyle}>Logged: {formatMinutes(finishRequirement?.workLogMinutes)}</span>
            <span style={summaryPillStyle}>Required: {formatMinutes(finishRequirement?.targetWorkedMinutes || 480)}</span>
          </div>
          <textarea
            value={finishReason}
            onChange={(event) => setFinishReason(event.target.value)}
            placeholder="Please type the reason, or cancel and fix your work log details."
            rows={4}
            style={textareaStyle}
          />
          {message && <div style={{ color: '#dc2626', fontSize: 13 }}>{message}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Button variant="secondary" onClick={() => setFinishReasonOpen(false)}>Cancel</Button>
            <Button loading={finishMutation.isPending} onClick={submitFinishReason}>Submit & Finish</Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

function ActionButton({ icon, label, onClick, disabled, primary = false }: { icon: React.ReactNode; label: string; onClick: () => void; disabled: boolean; primary?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        height: 34,
        padding: '0 10px',
        border: primary ? '1px solid #0f766e' : '1px solid #e2e8f0',
        borderRadius: 6,
        background: primary ? '#0f766e' : '#f8fafc',
        color: primary ? '#ffffff' : '#0f172a',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 13,
        fontWeight: 700,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {icon}
      {label}
    </button>
  )
}

const metricStyle: React.CSSProperties = {
  display: 'block',
  color: '#0f172a',
  marginTop: 2,
}

const optionStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: 12,
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  background: '#f8fafc',
  color: '#0f172a',
  fontSize: 14,
  lineHeight: 1.45,
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  boxSizing: 'border-box',
}

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  marginTop: 10,
  fontFamily: 'inherit',
}

const summaryPillStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 28,
  padding: '0 10px',
  borderRadius: 6,
  background: '#eff6ff',
  color: '#1d4ed8',
  fontSize: 12,
  fontWeight: 800,
}

const motivationStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  marginTop: 10,
  padding: 10,
  borderRadius: 8,
  background: '#ecfdf5',
  border: '1px solid #bbf7d0',
  color: '#065f46',
  fontSize: 12,
  lineHeight: 1.4,
}

const breakListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  marginTop: 12,
  padding: 10,
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  background: '#f8fafc',
}

const breakListTitleStyle: React.CSSProperties = {
  color: '#0f172a',
  fontSize: 12,
  fontWeight: 800,
}

const breakRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto',
  alignItems: 'center',
  gap: 7,
  color: '#475569',
  fontSize: 11,
}
